import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Box,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import { Add, Edit, Delete, ArrowDropDown, ArrowBack } from "@mui/icons-material";
import { toast } from "react-toastify";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { IoCloseCircleOutline } from "react-icons/io5";
import useKothar from "context/useKothar";

const DEFAULT_TEMPLATE_BODY = `<p>Thank you for contacting Kothar Educational Services. If you have any questions or need further assistance, please feel free to reach out, we'll be happy to help.</p>
<p><br></p>
<p>Administrative officer</p>
<p>________________________________</p>
<p><strong>KOTHAR EDUCATIONAL SERVICES</strong></p>
<p>info@kotharedu.com</p>
<p>0450 466 876 | 0480 322 403</p>
<p>https://kotharedu.com/ Office - 273/398-403 Pitt Street Haymarket Sydney</p>`;

const EmailTemplatesModal = ({ open, onClose, onTemplateSelect, refetchTemplates }) => {
  const [view, setView] = useState("list"); // "list" or "form"
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    templateName: "",
    subject: "",
    body: DEFAULT_TEMPLATE_BODY,
    attachments: [],
  });

  // For placeholder menu
  const [anchorEl, setAnchorEl] = useState(null);
  const subjectRef = useRef();
  const quillRef = useRef();
  const [anchorElBody, setAnchorElBody] = useState(null);

  const [{ emailTemplates, isEmailTemplatesLoading }, { refetchEmailTemplates }] = useKothar();

  useEffect(() => {
    if (open) refetchEmailTemplates();
  }, [open, refetchEmailTemplates]);

  const getPlaceholderData = async () => {
    const response = await axios.get("/email-templates/placeholders/client");
    return response?.data;
  };

  const { data: placeholderData = [] } = useQuery({
    queryKey: ["placeholderData"],
    queryFn: getPlaceholderData,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: open,
  });

  const { mutate: addEmailTemplate } = useMutation({
    mutationFn: (payload) => axios.post("/email-templates", payload),
    onSuccess: () => {
      toast.success("Email template added");
      refetchEmailTemplates();
      if (refetchTemplates) refetchTemplates();
      setView("list");
      setEditId(null);
      setForm({ templateName: "", subject: "", body: DEFAULT_TEMPLATE_BODY, attachments: [] });
    },
    onError: () => {
      toast.error("Failed to add email template");
    },
  });

  const { mutate: updateEmailTemplate } = useMutation({
    mutationFn: (payload) => axios.put(`/email-templates/${editId}`, payload),
    onSuccess: () => {
      toast.success("Email template updated");
      refetchEmailTemplates();
      if (refetchTemplates) refetchTemplates();
      setView("list");
      setEditId(null);
      setForm({ templateName: "", subject: "", body: DEFAULT_TEMPLATE_BODY, attachments: [] });
    },
    onError: () => {
      toast.error("Failed to update email template");
    },
  });

  const handleOpenForm = (template) => {
    if (template) {
      setForm({
        templateName: template.templateName,
        subject: template.subject,
        body: template.body,
        attachments: template.attachments || [],
      });
      setEditId(template.id);
    } else {
      setForm({ templateName: "", subject: "", body: DEFAULT_TEMPLATE_BODY, attachments: [] });
      setEditId(null);
    }
    setView("form");
  };

  const handleBackToList = () => {
    setView("list");
    setEditId(null);
    setForm({ templateName: "", subject: "", body: DEFAULT_TEMPLATE_BODY, attachments: [] });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setForm((prev) => ({ ...prev, attachments: Array.from(e.target.files) }));
  };

  const handleSave = () => {
    if (!form.templateName || !form.subject || !form.body) {
      toast.error("Please fill all required fields");
      return;
    }
    if (editId) {
      updateEmailTemplate({
        subject: form.subject,
        templateName: form.templateName,
        body: form.body || "",
        attachments: form.attachments || [],
      });
    } else {
      addEmailTemplate({
        subject: form.subject,
        templateName: form.templateName,
        body: form.body || "",
        attachments: form.attachments || [],
      });
    }
  };

  const { mutate: deleteEmailTemplate } = useMutation({
    mutationFn: (id) => axios.delete(`/email-templates/${id}`),
    onSuccess: () => {
      toast.success("Email template deleted");
      refetchEmailTemplates();
      if (refetchTemplates) refetchTemplates();
    },
    onError: () => {
      toast.error("Failed to delete email template");
    },
  });

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      deleteEmailTemplate(id);
    }
  };

  const handleSelectTemplate = (template) => {
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
    handleClose();
  };

  // Placeholder menu handlers
  const handlePlaceholderMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePlaceholderMenuClose = () => {
    setAnchorEl(null);
  };
  const handleInsertPlaceholder = (placeholder) => {
    const ref = subjectRef.current;
    if (ref) {
      const start = ref.selectionStart;
      const end = ref.selectionEnd;
      const before = form.subject.slice(0, start);
      const after = form.subject.slice(end);
      const insert = `{${placeholder}}`;
      setForm((prev) => ({
        ...prev,
        subject: before + insert + after,
      }));
      setTimeout(() => {
        ref.focus();
        ref.setSelectionRange(start + insert.length, start + insert.length);
      }, 0);
    }
    handlePlaceholderMenuClose();
  };

  const handleInsertBodyPlaceholder = (placeholder) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertText(range.index, `{${placeholder}}`);
      quill.setSelection(range.index + `{${placeholder}}`.length);
    }
    setAnchorElBody(null);
  };

  const handleClose = () => {
    setView("list");
    setEditId(null);
    setForm({ templateName: "", subject: "", body: DEFAULT_TEMPLATE_BODY, attachments: [] });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: "90vh", maxHeight: "90vh" },
      }}
    >
      <div className="flex items-center justify-between border-b border-primary px-4 py-4">
        <Box display="flex" alignItems="center" gap={2}>
          {view === "form" && (
            <IconButton onClick={handleBackToList} size="small">
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h6">
            {view === "form"
              ? editId
                ? "Edit Email Template"
                : "Add Email Template"
              : "Email Templates"}
          </Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <IoCloseCircleOutline className="text-4xl" />
        </IconButton>
      </div>
      <DialogContent sx={{ p: 3, overflow: "auto" }}>
        {view === "list" ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <Typography variant="body2" color="text.secondary">
                Select a template or create a new one
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenForm()}>
                Add Email Template
              </Button>
            </div>

            <Grid container spacing={3}>
              {emailTemplates?.length > 0 ? (
                emailTemplates.map((template) => (
                  <Grid item xs={12} sm={6} md={6} key={template.id}>
                    <Card
                      className="shadow-sm h-full flex flex-col border border-app-border cursor-pointer hover:shadow-md transition-shadow"
                      sx={{ borderRadius: 2 }}
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={2}
                        pt={2}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 180,
                          }}
                        >
                          {template.templateName}
                        </Typography>
                        <Box onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenForm(template)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(template.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <CardContent className="flex-1 flex flex-col pt-0">
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          gutterBottom
                        >
                          <div
                            dangerouslySetInnerHTML={{ __html: template.subject }}
                            className="whitespace-pre-line"
                          />
                        </Typography>
                        <Typography
                          variant="body2"
                          className="whitespace-pre-line text-app-text flex-1"
                          sx={{ minHeight: 80, fontSize: 15 }}
                        >
                          <div
                            dangerouslySetInnerHTML={{ __html: template.body }}
                            className="whitespace-pre-line"
                          />
                        </Typography>
                      </CardContent>
                      <Divider />
                      <Box
                        px={2}
                        py={1}
                        display="flex"
                        alignItems="center"
                        fontSize={13}
                        color="text.secondary"
                      >
                        <span>
                          <b>{template.attachments?.length || 0}</b> Attachment
                          {template.attachments?.length === 1 ? "" : "s"}
                        </span>
                      </Box>
                    </Card>
                  </Grid>
                ))
              ) : (
                <div className="flex justify-center items-center h-[40vh] w-full">
                  <Typography variant="h6" color="text.secondary">
                    No email templates found
                  </Typography>
                </div>
              )}
            </Grid>
          </>
        ) : (
          <div className="flex flex-col overflow-auto">
            <InputField
              label="Title"
              name="templateName"
              value={form.templateName}
              onChange={handleFormChange}
              fullWidth
              outerClassName="mb-4"
              required
            />

            {/* Subject with Placeholders */}
            <div className="flex flex-col mb-4 rounded-md border border-app-border p-2">
              <div className="flex items-center gap-2 mb-2">
                <label className="block uppercase text-app-text-secondary text-xs font-bold">
                  Subject <sup className="text-red-500">*</sup>
                </label>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  aria-controls={anchorEl ? "placeholder-menu" : undefined}
                  aria-haspopup="true"
                  onClick={handlePlaceholderMenuOpen}
                  sx={{ minWidth: 0, px: 1, height: 40, border: "none" }}
                  endIcon={<ArrowDropDown />}
                >
                  Placeholders
                </Button>
                <Menu
                  id="placeholder-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handlePlaceholderMenuClose}
                >
                  {placeholderData?.map((ph) => (
                    <MenuItem key={ph} onClick={() => handleInsertPlaceholder(ph)}>
                      {ph}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
              <InputField
                name="subject"
                value={form.subject}
                onChange={handleFormChange}
                fullWidth
                required
                ref={subjectRef}
                className="py-4"
                placeholder="Subject"
              />
            </div>

            <div className="flex items-center gap-2 mb-2">
              <label>
                Body <sup className="text-red-500">*</sup>
              </label>
              <Button
                onClick={(e) => setAnchorElBody(e.currentTarget)}
                variant="outlined"
                color="primary"
                size="small"
                endIcon={<ArrowDropDown />}
              >
                Placeholders
              </Button>
            </div>
            <Menu
              anchorEl={anchorElBody}
              open={Boolean(anchorElBody)}
              onClose={() => setAnchorElBody(null)}
            >
              {placeholderData?.map((ph) => (
                <MenuItem key={ph} onClick={() => handleInsertBodyPlaceholder(ph)}>
                  {ph}
                </MenuItem>
              ))}
            </Menu>
            <ReactQuill
              ref={quillRef}
              value={form.body}
              onChange={(val) => setForm((prev) => ({ ...prev, body: val }))}
              style={{ minHeight: 120, marginBottom: 10 }}
            />
            <UploadFile
              label="Attachments"
              name="attachments"
              value={form.attachments}
              onChange={handleFileChange}
              multiple
              className="mt-4"
            />

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={handleBackToList} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                disabled={!form.templateName || !form.subject || !form.body}
              >
                Save
              </Button>
            </Box>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailTemplatesModal;
